import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  GoogleMap,
  Autocomplete,
  Marker
} from "@react-google-maps/api";
import { useMaps } from "./MapsProvider";
import { FiX, FiMapPin, FiNavigation } from "react-icons/fi";

const containerStyle = {
  width: "100%",
  height: "450px"
};

// Lasmari coordinates (will be used as fallback)
const lasmariCoords = {
  lat: 39.796276,
  lng: 19.884997
};

// Corfu boundaries - restrict selection to this area
const CORFU_BOUNDS = {
  north: 39.850, // Northern tip of Corfu
  south: 39.260, // Southern tip of Corfu (expanded further south)
  east: 20.140,  // Eastern side of Corfu (expanded further east)
  west: 19.570   // Western side of Corfu 
};

// Function to check if a location is within Corfu bounds
const isWithinCorfuBounds = (lat, lng) => {
  return (
    lat >= CORFU_BOUNDS.south &&
    lat <= CORFU_BOUNDS.north &&
    lng >= CORFU_BOUNDS.west &&
    lng <= CORFU_BOUNDS.east
  );
};

// Dark map styles - UNUSED, commenting out to fix linter warning
/*
const mapStyles = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#bdbdbd" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels.text", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#181818" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "poi.park", elementType: "labels.text.stroke", stylers: [{ color: "#1b1b1b" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#373737" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
  { featureType: "road.highway.controlled_access", elementType: "geometry", stylers: [{ color: "#4e4e4e" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
  { featureType: "water", elementType: "labels.text", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] }
];
*/

const DropoffMapModal = ({ isOpen, onClose, onConfirm, initialLocation }) => {
  const { isLoaded, createMarkerIcon, mapOptions, defaultLocation } = useMaps();
  
  // Use defaultLocation from context instead of hardcoded values
  const defaultCoords = defaultLocation ? 
    { lat: defaultLocation.lat, lng: defaultLocation.lng } : 
    lasmariCoords;
  
  const defaultName = defaultLocation?.name || "Lasmari Apraos";
  
  const [selectedLocation, setSelectedLocation] = useState(
    initialLocation || { name: defaultName, position: defaultCoords }
  );
  const [center, setCenter] = useState(
    initialLocation?.position || defaultCoords
  );
  const [errorMessage, setErrorMessage] = useState(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const observerRef = useRef(null);
  const eventListenersRef = useRef([]);

  // Initialize selectedLocation with default location name on first load
  useEffect(() => {
    if (!initialLocation && !selectedLocation) {
      setSelectedLocation({
        name: defaultName,
        position: defaultCoords
      });
    }
  }, [defaultLocation]);

  // Reset the selected location when the modal opens
  useEffect(() => {
    if (isOpen) {
      // If initialLocation is null, default to context default location
      if (!initialLocation) {
        setSelectedLocation({
          name: defaultName,
          position: defaultCoords
        });
      } else {
        setSelectedLocation(initialLocation);
      }
      
      setErrorMessage(null);
      
      // Reset the position based on initialLocation or to default if none
      const position = initialLocation?.position || defaultCoords;
      setCenter(position);
    }
  }, [isOpen, initialLocation, defaultLocation]);

  // Set map center based on initial location or default to context location
  useEffect(() => {
    if (initialLocation && initialLocation.position) {
      setCenter(initialLocation.position);
    } else {
      setCenter(defaultCoords);
    }
  }, [initialLocation, defaultLocation]);

  // SIMPLE CLEANUP - Just like standalone page
  useEffect(() => {
    if (!isOpen) {
      console.log('Simple dropoff modal cleanup');
    }
  }, [isOpen]);

  const handlePlaceSelect = () => {
    try {
      if (!autocompleteRef.current) {
        console.warn("Dropoff autocomplete reference not available");
        return;
      }

      console.log('Dropoff place selection triggered');
      const place = autocompleteRef.current.getPlace();
      console.log('Dropoff place data:', place);

      if (place && place.geometry && place.geometry.location) {
        const position = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        
        // Verify the location is within Corfu boundaries
        if (!isWithinCorfuBounds(position.lat, position.lng)) {
          setErrorMessage("Selected location is outside Corfu island. Please select a location within Corfu.");
          return;
        }

        setSelectedLocation({
          name: place.formatted_address || place.name,
          position
        });

        setCenter(position);
        setErrorMessage(null);
      }
    } catch (error) {
      console.error("Error handling place selection:", error);
      setErrorMessage("Error selecting location. Please try again.");
    }
  };

  const handleMapClick = (event) => {
    try {
      if (!event || !event.latLng) {
        console.warn("Invalid map click event");
        return;
      }

      const clickedPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      
      // Verify the location is within Corfu boundaries
      if (!isWithinCorfuBounds(clickedPosition.lat, clickedPosition.lng)) {
        setErrorMessage("Selected location is outside Corfu island. Please select a location within Corfu.");
        return;
      }

      setSelectedLocation({
        name: "Selected Location",
        position: clickedPosition
      });
      
      setErrorMessage(null);
      reverseGeocode(clickedPosition);
    } catch (error) {
      console.error("Error handling map click:", error);
      setErrorMessage("Error selecting location. Please try again.");
    }
  };

  const reverseGeocode = (position) => {
    try {
      if (!window.google || !window.google.maps) {
        console.warn("Google Maps not available for reverse geocoding");
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      
      geocoder.geocode({ location: position }, (results, status) => {
        try {
          if (status === "OK" && results && results[0]) {
            setSelectedLocation({
              name: results[0].formatted_address,
              position
            });
          }
        } catch (error) {
          console.error("Error processing geocoding results:", error);
          // Keep the position but with generic name
          setSelectedLocation({
            name: "Selected Location",
            position
          });
        }
      });
    } catch (error) {
      console.error("Error setting up reverse geocoding:", error);
      // Keep the position but with generic name
      setSelectedLocation({
        name: "Selected Location",
        position
      });
    }
  };

  const handleConfirm = () => {
    if (selectedLocation) {
      // Double-check the location is within Corfu boundaries
      const { lat, lng } = selectedLocation.position;
      if (!isWithinCorfuBounds(lat, lng)) {
        setErrorMessage("Selected location is outside Corfu island. Please select a location within Corfu.");
        return;
      }
      
      onConfirm(selectedLocation);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setErrorMessage("Geolocation is not supported by your browser. Please select your location manually.");
      return;
    }

    // Check if we're on HTTPS (required for mobile browsers)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setErrorMessage("Location services require a secure connection. Please use HTTPS or select your location manually.");
      return;
    }

    setErrorMessage("Getting your location...");

    // Enhanced geolocation options for better mobile compatibility
    const options = {
      enableHighAccuracy: false, // Set to false for better mobile compatibility
      timeout: 15000, // 15 seconds timeout (mobile can be slower)
      maximumAge: 300000 // 5 minutes cache (300000ms)
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        console.log("Got user location:", userPosition);
        
        // Verify the location is within Corfu boundaries
        if (!isWithinCorfuBounds(userPosition.lat, userPosition.lng)) {
          setErrorMessage("Your current location is outside Corfu island. Please select a location within Corfu.");
          return;
        }
        
        setCenter(userPosition);
        setSelectedLocation({
          name: "Your Location",
          position: userPosition
        });
        
        setErrorMessage(null);
        reverseGeocode(userPosition);
      },
      (error) => {
        console.error("Geolocation error:", error);
        
        let errorMsg = "Could not get your location. ";
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg += "Location permission was denied. Please enable location services in your browser settings and try again, or select your location manually.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg += "Location information is unavailable. Please check your GPS/location services and try again, or select your location manually.";
            break;
          case error.TIMEOUT:
            errorMsg += "Location request timed out. Please try again or select your location manually.";
            break;
          default:
            errorMsg += "An unknown error occurred. Please try again or select your location manually.";
            break;
        }
        
        setErrorMessage(errorMsg);
      },
      options
    );
  };

  const handleMarkerPositionChange = (position) => {
    setCenter(position);
    
    // Check if position matches default coordinates (with some tolerance for floating point)
    const isDefaultLocation = 
      Math.abs(position.lat - defaultCoords.lat) < 0.0001 && 
      Math.abs(position.lng - defaultCoords.lng) < 0.0001;
      
    // If it's the default location, use that name instead of "Selected Location"
    setSelectedLocation({
      name: isDefaultLocation ? defaultName : "Selected Location",
      position
    });
    
    setErrorMessage(null);
    
    // Only reverseGeocode if it's not the default location
    if (!isDefaultLocation) {
      reverseGeocode(position);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-80 backdrop-blur-sm">
      <div className="mobile-map-modal max-w-4xl w-full max-h-[90vh] sm:max-h-[85vh] lg:max-h-[90vh] overflow-hidden bg-white backdrop-blur-xl rounded-xl border border-black/10 shadow-2xl flex flex-col">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-3 sm:p-4 lg:p-5 border-b border-black/10 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-semibold text-[#1a1012]">Select Dropoff Location</h2>
          <button
            onClick={onClose}
            className="text-[#5b4d4f] hover:text-[#ba1609] transition-colors p-1"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="flex-shrink-0 mx-3 sm:mx-4 lg:mx-5 mt-3 sm:mt-4 p-3 bg-[#fbeceb] border border-[#ba1609]/40 rounded-lg">
            <p className="text-[#8e1107] text-sm">{errorMessage}</p>
          </div>
        )}

        {isLoaded ? (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Content Area - Scrollable */}
            <div className="flex-1 p-3 sm:p-4 lg:p-5 space-y-3 sm:space-y-4 overflow-y-auto min-h-0">
              <p className="text-sm text-[#5b4d4f]">
                <span className="inline-block w-2 h-2 bg-[#ba1609] rounded-full mr-2"></span>
                All locations restricted to Corfu island
              </p>

                            {/* Search input */}
              <div className="autocomplete-container autocomplete-container-dropoff relative">
                <Autocomplete
                  onLoad={(autocomplete) => {
                    console.log('Dropoff autocomplete loading...', window.location.pathname);
                    console.log('Google Maps API check:', !!window.google?.maps?.places?.Autocomplete);
                    console.log('Autocomplete instance:', autocomplete);
                    autocompleteRef.current = autocomplete;
                    
                    // Enhanced Corfu bounds restriction
                    try {
                      const corfuBounds = new window.google.maps.LatLngBounds(
                        new window.google.maps.LatLng(CORFU_BOUNDS.south, CORFU_BOUNDS.west),
                        new window.google.maps.LatLng(CORFU_BOUNDS.north, CORFU_BOUNDS.east)
                      );
                      
                      autocomplete.setBounds(corfuBounds);
                      autocomplete.setComponentRestrictions({ country: 'gr' });
                      
                      // KEEP IT SIMPLE - Just like standalone page  
                      console.log('Simple autocomplete setup for dropoff - no complex positioning');
                    } catch (e) {
                      console.warn('Could not set autocomplete bounds:', e);
                    }
                  }}
                  onPlaceChanged={handlePlaceSelect}
                  options={{
                    fields: ['place_id', 'geometry', 'name', 'formatted_address'],
                    strictBounds: true,
                    componentRestrictions: { country: 'gr' }
                  }}
                >
                  <div className="relative">
                    <FiMapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#ba1609] z-10" size={18} />
                    <input
                      type="text"
                      placeholder="Search for a location in Corfu..."
                      className="w-full pl-10 pr-4 py-3 bg-white border border-black/10 rounded-lg text-[#1a1012] placeholder-[#5b4d4f] focus:outline-none focus:ring-2 focus:ring-[#ba1609]/50 focus:border-[#ba1609]"
                      defaultValue=""
                    />
                  </div>
                </Autocomplete>
                
                {/* SIMPLE STYLING - Just like standalone page */}
                <style jsx global>{`
                  .pac-container {
                    z-index: 10000 !important;
                  }
                `}</style>
              </div>

              {/* Current location button */}
              <button
                onClick={handleUseMyLocation}
                disabled={false} // isGettingLocation is not defined in the original code, so it's always false
                className="w-full flex items-center justify-center px-4 py-3 bg-white border border-[#ba1609] rounded-lg text-[#ba1609] hover:bg-[#fbeceb] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiNavigation className="mr-2" size={18} />
                Use Current Location
              </button>

              {/* Map Container - Dynamic Height */}
              <div className="relative rounded-lg overflow-hidden border border-black/10" style={{ height: 'calc(40vh - 100px)', minHeight: '250px', maxHeight: '400px' }}>
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={center}
                  zoom={14}
                  options={{
                    ...mapOptions,
                    restriction: {
                      latLngBounds: {
                        north: CORFU_BOUNDS.north,
                        south: CORFU_BOUNDS.south,
                        east: CORFU_BOUNDS.east,
                        west: CORFU_BOUNDS.west,
                      },
                      strictBounds: true,
                    },
                    maxZoom: 18,
                    minZoom: 11,
                  }}
                  onClick={handleMapClick}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                >
                  {selectedLocation && selectedLocation.position && (
                    <Marker
                      position={selectedLocation.position}
                      icon={createMarkerIcon('red')}
                      title={selectedLocation.name || 'Selected Location'}
                    />
                  )}
                </GoogleMap>
              </div>

              {/* Selected location display */}
              {selectedLocation && (
                <div className="p-3 bg-[#fbeceb] rounded-lg border border-[#ba1609]/30">
                  <p className="text-[#1a1012] font-medium">{selectedLocation.name}</p>
                  <p className="text-[#5b4d4f] text-sm mt-1">
                    Lat: {selectedLocation.position.lat.toFixed(6)}, 
                    Lng: {selectedLocation.position.lng.toFixed(6)}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[250px]">
            <div className="animate-spin h-10 w-10 border-4 border-[#ba1609] rounded-full border-t-transparent"></div>
          </div>
        )}

        {/* Action buttons - Always Fixed at Bottom */}
        <div className="flex-shrink-0 p-3 sm:p-4 lg:p-5 border-t border-black/10 bg-[#faf7f6]">
          <div className="flex justify-between">
            <button
              onClick={onClose}
              className="px-4 sm:px-5 py-2 border border-black/10 text-[#1a1012] rounded-lg hover:bg-[#fbeceb] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className={`px-4 sm:px-5 py-2 rounded-lg transition-colors ${
                selectedLocation && !errorMessage
                  ? "bg-[#ba1609] hover:bg-[#8e1107] text-white"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
              disabled={!selectedLocation || errorMessage}
            >
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DropoffMapModal;